import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Card,
  CardContent,
  Autocomplete,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  CalendarToday as CalendarIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import * as api from '../../api/api';
import { markRatesUpdated } from '../../utils/cacheInvalidation';
import { 
  calculateCOLAAdjustedRate, 
  validateMemberRoleMapping, 
  getRosterRolesForRateRole,
  doesRosterRoleMapToRateRole,
  getStandardizedRole,
  doesMemberMapToRate 
} from '../../utils/rateUtils';

function RatesTab() {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState('All');
  const [locationFilter, setLocationFilter] = useState('All');
  const [yearFilter, setYearFilter] = useState('All');
    const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    company: '',
    function: '',
    role: '',
    location: '',
    dailyRate: '',
    annualWorkingDays: '',
    year: new Date().getFullYear()
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [editRateForm, setEditRateForm] = useState({
    company: '',
    function: '',
    role: '',
    location: '',
    dailyRate: '',
    annualWorkingDays: '',
    year: new Date().getFullYear(),
    startDate: '',
    endDate: '',
    status: 'Active'
  });
  const [dialogOpen, setDialogOpen] = useState(false);
      const [newRate, setNewRate] = useState({
    company: '',
    function: '',
    role: '',
    location: '',
    dailyRate: '',
    annualWorkingDays: '',
    year: new Date().getFullYear(),
    startDate: '',
    endDate: '',
    status: 'Active'
  });
  const [addYearDialogOpen, setAddYearDialogOpen] = useState(false);
  const [selectedRoleGroup, setSelectedRoleGroup] = useState(null);
  const [newYearRate, setNewYearRate] = useState({
    year: new Date().getFullYear(),
    dailyRate: '',
    annualWorkingDays: ''
  });
    const [companyOptions, setCompanyOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [functionOptions, setFunctionOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);
    const [colaRate, setCOLARate] = useState(4.5);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState('All');
    const [editingRoleName, setEditingRoleName] = useState(false);
  const [tempRoleName, setTempRoleName] = useState('');
  const [propagatingChanges, setPropagatingChanges] = useState(false);

  const fetchRates = async () => {
    try {
      setLoading(true);
      const response = await api.getRates();
      const ratesData = response?.rates || [];
      
            // Ensure all rates have a year field and status (backward compatibility)
      const ratesWithYear = ratesData.map(rate => ({
        ...rate,
        year: rate.year || 2025,
        status: rate.status || 'Active'
      }));
      
      setRates(ratesWithYear);
      
                  // Extract unique options for dropdown menus
      const uniqueCompanies = [...new Set(ratesWithYear.map(rate => rate.company).filter(Boolean))].sort();
      const uniqueLocations = [...new Set([...ratesWithYear.map(rate => rate.location).filter(Boolean), 'GER'])].sort();
      const uniqueFunctions = [...new Set(ratesWithYear.map(rate => rate.function).filter(Boolean))].sort();
      const uniqueRoles = [...new Set(ratesWithYear.map(rate => rate.role).filter(Boolean))].sort();
      const uniqueYears = [...new Set(ratesWithYear.map(rate => rate.year))].sort((a, b) => b - a);
      
      setCompanyOptions(uniqueCompanies);
      setLocationOptions(uniqueLocations);
      setFunctionOptions(uniqueFunctions);
      setRoleOptions(uniqueRoles);
      setYearOptions(uniqueYears);
      
      setError('');
    } catch (err) {
      console.error('Error fetching rates:', err);
      setError('Failed to load rates');
      setRates([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch COLA rate
    const fetchCOLARate = async () => {
    try {
      const settings = await api.getCOLASettings();
      setCOLARate(settings.annual_cola_percentage || 4.5);
    } catch (error) {
      console.warn('Could not fetch COLA rate, using default');
      setCOLARate(4.5);
    }
  };

  // Helper function to find members affected by a rate change
  const findMembersAffectedByRate = async (rateInfo) => {
    try {
      const rosterResponse = await api.getITRoster();
      const rosterData = rosterResponse.data || rosterResponse || [];
      
      const affectedMembers = rosterData.filter(member => 
        doesMemberMapToRate(member, rateInfo)
      );
      
      console.log(`Found ${affectedMembers.length} members affected by rate change for ${rateInfo.company}/${rateInfo.location}/${rateInfo.role}`);
      
      return affectedMembers;
    } catch (error) {
      console.error('Error fetching IT roster for rate propagation:', error);
      return [];
    }
  };

  // Propagate member changes to all outcomes where affected members are staffed
  const propagateRateChangeToOutcomes = async (rateInfo, affectedMembers) => {
    if (affectedMembers.length === 0) {
      console.log('No members affected by rate change, skipping outcome propagation');
      return { success: true, totalUpdated: 0 };
    }

    console.log(`🔄 Starting rate change propagation for ${affectedMembers.length} members`);
    setPropagatingChanges(true);
    
    try {
      let totalOutcomesUpdated = 0;
      let totalMembersProcessed = 0;
      let propagationErrors = [];
      
      // Process members in batches to avoid overwhelming the system
      const BATCH_SIZE = 3;
      const memberBatches = [];
      for (let i = 0; i < affectedMembers.length; i += BATCH_SIZE) {
        memberBatches.push(affectedMembers.slice(i, i + BATCH_SIZE));
      }
      
      for (let batchIndex = 0; batchIndex < memberBatches.length; batchIndex++) {
        const batch = memberBatches[batchIndex];
        console.log(`Processing member batch ${batchIndex + 1}/${memberBatches.length}: ${batch.length} members`);
        
        // Process batch members in parallel
        const batchPromises = batch.map(async (member) => {
          try {
            // Find all outcomes where this member is staffed
            const outcomesResponse = await api.findOutcomesWithMember(member.id);
            const memberOutcomes = outcomesResponse.outcomes || [];
            
            if (memberOutcomes.length === 0) {
              console.log(`Member ${member.name} is not assigned to any outcomes`);
              return { memberName: member.name, outcomesUpdated: 0, success: true };
            }
            
            console.log(`Member ${member.name} is assigned to ${memberOutcomes.length} outcomes`);
            
            // Prepare member update data with current information
            const memberUpdateData = {
              id: member.id,
              name: member.name,
              company: member.company,
              location: member.location,
              role: member.role,
              function: member.function,
              focus: member.focus,
              department: member.department
            };
            
            // Update member information in each outcome
            let memberOutcomesUpdated = 0;
            const memberUpdateErrors = [];
            
            for (const outcome of memberOutcomes) {
              try {
                await api.updateMemberInOutcome(outcome.id, memberUpdateData);
                memberOutcomesUpdated++;
              } catch (outcomeError) {
                console.error(`Failed to update member ${member.name} in outcome ${outcome.id}:`, outcomeError);
                memberUpdateErrors.push(`Outcome ${outcome.name || outcome.id}: ${outcomeError.message}`);
              }
            }
            
            console.log(`✅ Updated member ${member.name} in ${memberOutcomesUpdated}/${memberOutcomes.length} outcomes`);
            
            return {
              memberName: member.name,
              outcomesUpdated: memberOutcomesUpdated,
              totalOutcomes: memberOutcomes.length,
              errors: memberUpdateErrors,
              success: memberUpdateErrors.length === 0
            };
            
          } catch (memberError) {
            console.error(`Error processing member ${member.name}:`, memberError);
            return {
              memberName: member.name,
              outcomesUpdated: 0,
              success: false,
              error: memberError.message
            };
          }
        });
        
        // Wait for batch to complete
        const batchResults = await Promise.all(batchPromises);
        
        // Aggregate results
        batchResults.forEach(result => {
          totalMembersProcessed++;
          totalOutcomesUpdated += result.outcomesUpdated || 0;
          
          if (!result.success) {
            propagationErrors.push(`${result.memberName}: ${result.error || 'Unknown error'}`);
          }
          
          if (result.errors && result.errors.length > 0) {
            propagationErrors.push(...result.errors.map(err => `${result.memberName} - ${err}`));
          }
        });
        
        // Small delay between batches
        if (batchIndex < memberBatches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
      
      console.log(`✅ Rate change propagation completed:`);
      console.log(`   Rate: ${rateInfo.company}/${rateInfo.location}/${rateInfo.role}`);
      console.log(`   Members processed: ${totalMembersProcessed}/${affectedMembers.length}`);
      console.log(`   Total outcomes updated: ${totalOutcomesUpdated}`);
      
      if (propagationErrors.length > 0) {
        console.warn(`⚠️ Propagation errors (${propagationErrors.length}):`, propagationErrors.slice(0, 5));
      }
      
      return {
        success: propagationErrors.length === 0,
        totalUpdated: totalOutcomesUpdated,
        membersProcessed: totalMembersProcessed,
        errors: propagationErrors
      };
      
    } catch (error) {
      console.error('Error in rate change propagation:', error);
      return {
        success: false,
        error: error.message,
        totalUpdated: 0
      };
    } finally {
      setPropagatingChanges(false);
    }
  };

  useEffect(() => {
    fetchRates();
    fetchCOLARate();
  }, []);

  // Group rates by company, location, and role
  const groupRatesByRole = () => {
    const grouped = {};
    
    rates.forEach((rate, index) => {
      const key = `${rate.company}-${rate.location}-${rate.role}`;
      if (!grouped[key]) {
        grouped[key] = {
          company: rate.company,
          location: rate.location,
          function: rate.function,
          role: rate.role,
          years: []
        };
      }
      
      grouped[key].years.push({
        ...rate,
        originalIndex: index
      });
    });
    
    // Sort years within each group (newest first)
    Object.values(grouped).forEach(group => {
      group.years.sort((a, b) => b.year - a.year);
    });
    
    return grouped;
  };

    const handleEdit = (rate, index) => {
    setEditingId(index);
    setEditForm({
      company: rate.company,
      function: rate.function,
      role: rate.role || '',
      location: rate.location,
      dailyRate: rate.dailyRate.toString(),
      annualWorkingDays: rate.annualWorkingDays.toString(),
      year: rate.year || 2025
    });
  };

    const handleOpenEditDialog = (rate, index) => {
    setEditingRate({ ...rate, originalIndex: index });
    setEditRateForm({
      company: rate.company,
      function: rate.function,
      role: rate.role || '',
      location: rate.location,
      dailyRate: rate.dailyRate.toString(),
      annualWorkingDays: rate.annualWorkingDays.toString(),
      year: rate.year || 2025,
      startDate: rate.startDate || '',
      endDate: rate.endDate || '',
      status: rate.status || 'Active'
    });
    setTempRoleName(rate.role || '');
    setEditingRoleName(false);
    setEditDialogOpen(true);
  };

    const handleSaveEditDialog = async () => {
    if (!editRateForm.company || !editRateForm.function || !editRateForm.role || !editRateForm.location || !editRateForm.dailyRate || !editRateForm.annualWorkingDays || !editRateForm.year) {
      setError('All required fields must be filled');
      return;
    }

    try {
      const updatedRate = {
        ...editRateForm,
        dailyRate: parseFloat(editRateForm.dailyRate),
        annualWorkingDays: parseInt(editRateForm.annualWorkingDays),
        year: parseInt(editRateForm.year)
      };

                  // Check if role name was changed
      const originalRole = editingRate.role;
      const newRole = editRateForm.role;
      const roleNameChanged = originalRole !== newRole;
      
      // Declare variables that will be used in success logging
      let ratesToUpdate = [];
      let membersToUpdate = [];
      let memberMappingIssues = [];
      let ratesUpdated = 0;
      let rateUpdateErrors = [];
      let membersUpdated = 0;
      let memberUpdateErrors = [];

      if (roleNameChanged) {
        console.log(`Role name changed from '${originalRole}' to '${newRole}' for ${editingRate.company}/${editingRate.location}`);
        
        // Validate new role name
        if (!newRole || newRole.trim() === '') {
          setError('Role name cannot be empty');
          return;
        }
        
                // Check if new role name already exists for this company/location
        const existingRoleConflict = rates.some(rate => 
          rate.company === editingRate.company && 
          rate.location === editingRate.location && 
          rate.role === newRole.trim() &&
          rate.role !== originalRole
        );
        
        if (existingRoleConflict) {
          setError(`Role '${newRole}' already exists for ${editingRate.company}/${editingRate.location}. Please choose a different name.`);
          return;
        }
        
        // Validate that the new role name makes sense for member mapping
        console.log(`Role name validation: Changing '${originalRole}' to '${newRole}' for ${editingRate.company}/${editingRate.location}`);
        
                                        // Find all rates and members that will be affected by this Company/Location/Role change
        ratesToUpdate = rates.filter(rate => 
          rate.company === editingRate.company && 
          rate.location === editingRate.location && 
          rate.role === originalRole
        );
        
        console.log(`Found ${ratesToUpdate.length} rates to update for ${editingRate.company}/${editingRate.location}/${originalRole} -> ${newRole}`);
        
        let rosterData = [];
        
        try {
          const rosterResponse = await api.getITRoster();
          rosterData = rosterResponse.data || rosterResponse || [];
          
          // Find members that map to this specific Company/Location/Role combination
          const originalRateTemplate = {
            company: editingRate.company,
            location: editingRate.location,
            role: originalRole
          };
          
          membersToUpdate = rosterData.filter(member => 
            doesMemberMapToRate(member, originalRateTemplate)
          );
          
          console.log(`Found ${membersToUpdate.length} IT roster members that map to ${editingRate.company}/${editingRate.location}/${originalRole}`);
          
          // Log the detailed mapping information
          if (membersToUpdate.length > 0) {
            console.log('Member mappings for this Company/Location/Role:', membersToUpdate.map(m => ({
              memberName: m.name,
              memberKey: `${m.company}/${m.location}/${m.role}`,
              memberRole: m.role,
              standardizedRole: getStandardizedRole(m.role),
              mapsToRateKey: `${editingRate.company}/${editingRate.location}/${originalRole}`,
              willUpdateRateTo: `${editingRate.company}/${editingRate.location}/${newRole}`
            })));
          }
          
          // Check if any members will have mapping issues after the rate role change
          for (const member of membersToUpdate) {
            // Create a test rate with the new role name to validate mapping
            const newRateTemplate = {
              company: editingRate.company,
              location: editingRate.location,
              role: newRole
            };
            
            // Check if the member will still map to the new rate
            const willStillMap = doesMemberMapToRate(member, newRateTemplate);
            
            if (!willStillMap) {
              // This member will lose their rate mapping after the change
              memberMappingIssues.push({
                memberName: member.name,
                memberKey: `${member.company}/${member.location}/${member.role}`,
                currentRateKey: `${editingRate.company}/${editingRate.location}/${originalRole}`,
                newRateKey: `${editingRate.company}/${editingRate.location}/${newRole}`,
                issue: `Member role '${member.role}' (standardized: '${getStandardizedRole(member.role)}') will not map to new rate role '${newRole}'`,
                suggestions: [
                  `Update member role to map to '${newRole}'`,
                  `Keep a rate for '${getStandardizedRole(member.role)}' at this location`,
                  `Verify role standardization mapping is correct`
                ]
              });
            }
          }
          
          if (memberMappingIssues.length > 0) {
            console.warn('⚠️ Member mapping issues detected after Company/Location/Role change:', memberMappingIssues);
            const issueDetails = memberMappingIssues.map(issue => 
              `${issue.memberName} (${issue.memberKey}): ${issue.issue}`
            ).join('; ');
            console.warn(`Warning: ${memberMappingIssues.length} members will lose rate mapping: ${issueDetails}`);
            
            // Show a more detailed warning to the user
            const confirmMessage = `Warning: Changing role from '${originalRole}' to '${newRole}' at ${editingRate.company}/${editingRate.location} will affect ${memberMappingIssues.length} roster members who may lose their rate mapping.\n\nAffected members: ${memberMappingIssues.map(i => i.memberName).join(', ')}\n\nDo you want to continue?`;
            
            if (!window.confirm(confirmMessage)) {
              return; // User cancelled the operation
            }
          }
          
        } catch (rosterError) {
          console.warn('Could not fetch IT roster for Company/Location/Role validation:', rosterError);
          // Continue with rate updates even if roster fetch fails
        }
        
                // Update all matching rates
        for (const rate of ratesToUpdate) {
          try {
            const rateIndex = rates.findIndex(r => 
              r.company === rate.company && 
              r.location === rate.location && 
              r.role === rate.role && 
              r.year === rate.year
            );
            
            if (rateIndex !== -1) {
              const updatedRateForBulk = {
                ...rate,
                role: newRole.trim(),
                // If this is the rate being edited, include all other changes
                ...(rateIndex === editingRate.originalIndex ? {
                  dailyRate: parseFloat(editRateForm.dailyRate),
                  annualWorkingDays: parseInt(editRateForm.annualWorkingDays),
                  year: parseInt(editRateForm.year),
                  startDate: editRateForm.startDate,
                  endDate: editRateForm.endDate,
                  status: editRateForm.status
                } : {})
              };
              
              await api.updateRate(rateIndex, updatedRateForBulk);
              ratesUpdated++;
            }
          } catch (rateError) {
            console.error(`Failed to update rate for year ${rate.year}:`, rateError);
            rateUpdateErrors.push(`Year ${rate.year}: ${rateError.message}`);
          }
        }
        
                                // Update IT Roster members - handle Company/Location/Role mapping carefully
        for (const member of membersToUpdate) {
          try {
            // Strategy for updating roster members when rate role changes:
            // 1. If member role exactly matches old rate role -> update to new rate role
            // 2. If member has specific roster role that maps to old rate role -> keep original role if it will still map to new rate role
            // 3. If member role won't map to new rate role -> update to new rate role to maintain mapping
            
            let newMemberRole = member.role;
            let updateReason = '';
            
            const newRateTemplate = {
              company: editingRate.company,
              location: editingRate.location,
              role: newRole
            };
            
            if (member.role === originalRole) {
              // Exact match - definitely update
              newMemberRole = newRole.trim();
              updateReason = 'exact role match';
            } else if (!doesMemberMapToRate(member, newRateTemplate)) {
              // Member's current role won't map to new rate role - need to update
              newMemberRole = newRole.trim();
              updateReason = 'maintain rate mapping';
            } else {
              // Member's role will still map to new rate role - keep original
              updateReason = 'role still maps correctly';
            }
            
            console.log(`Member ${member.name} (${member.company}/${member.location}/${member.role}): ${updateReason} -> '${newMemberRole}'`);
            
            const updatedMember = {
              ...member,
              role: newMemberRole
            };
            
            // Only update if the role actually changed
            if (newMemberRole !== member.role) {
              await api.updateITRosterMember(member.id, updatedMember);
              membersUpdated++;
              console.log(`✅ Updated ${member.name}: '${member.role}' -> '${newMemberRole}' (${updateReason})`);
            } else {
              console.log(`ℹ️ No change needed for ${member.name}: '${member.role}' (${updateReason})`);
            }
            
          } catch (memberError) {
            console.error(`Failed to update member ${member.name}:`, memberError);
            memberUpdateErrors.push(`${member.name}: ${memberError.message}`);
          }
        }
        
        // Report results
        console.log(`Role name update completed:`);
        console.log(`- Rates updated: ${ratesUpdated}/${ratesToUpdate.length}`);
        console.log(`- Members updated: ${membersUpdated}/${membersToUpdate.length}`);
        
        if (rateUpdateErrors.length > 0) {
          console.warn('Rate update errors:', rateUpdateErrors);
        }
        
        if (memberUpdateErrors.length > 0) {
          console.warn('Member update errors:', memberUpdateErrors);
        }
        
        // Show error if critical updates failed
        if (ratesUpdated === 0 && ratesToUpdate.length > 0) {
          setError('Failed to update rates with new role name');
          return;
        }
        
        // Show warning if some updates failed but continue
        if (rateUpdateErrors.length > 0 || memberUpdateErrors.length > 0) {
          console.warn('Some updates failed but continuing...');
        }
        
      } else {
        // No role name change, just update the single rate
              await api.updateRate(editingRate.originalIndex, updatedRate);
      markRatesUpdated();

      // Check if this rate change affects current year and should trigger member propagation
      const currentYear = new Date().getFullYear();
      const isCurrentYearRate = updatedRate.year === currentYear;
      
      if (isCurrentYearRate && !roleNameChanged) {
        console.log(`Rate updated for current year (${currentYear}), checking for member propagation...`);
        
        // Find members affected by this specific rate
        const rateInfo = {
          company: updatedRate.company,
          location: updatedRate.location,
          role: updatedRate.role
        };
        
        const affectedMembers = await findMembersAffectedByRate(rateInfo);
        
        if (affectedMembers.length > 0) {
          console.log(`Propagating rate change to outcomes for ${affectedMembers.length} affected members...`);
          
          // Propagate changes in the background
          propagateRateChangeToOutcomes(rateInfo, affectedMembers)
            .then(result => {
              if (result.success) {
                console.log(`✅ Rate change propagation completed: ${result.totalUpdated} outcome updates`);
              } else {
                console.warn(`⚠️ Rate change propagation completed with issues:`, result);
              }
              window.dispatchEvent(new CustomEvent('outcomeCachesInvalidated'));
            })
            .catch(error => {
              console.error(`❌ Rate change propagation failed:`, error);
            });
        }
      }
      }

      markRatesUpdated();
      setEditDialogOpen(false);
      setEditingRate(null);
      setEditingRoleName(false);
      setTempRoleName('');
      setEditRateForm({
        company: '',
        function: '',
        role: '',
        location: '',
        dailyRate: '',
        annualWorkingDays: '',
        year: new Date().getFullYear(),
        startDate: '',
        endDate: '',
        status: 'Active'
      });
      await fetchRates();
      setError('');
      
                      // Show success message with detailed update summary
        if (roleNameChanged) {
          console.log(`✅ Company/Location/Role update completed successfully:`);
          console.log(`   Location: ${editingRate.company}/${editingRate.location}`);
          console.log(`   Role: '${originalRole}' → '${newRole}'`);
          console.log(`   Summary: ${ratesUpdated}/${ratesToUpdate.length} rates updated, ${membersUpdated}/${membersToUpdate.length} members updated`);
          
          if (memberMappingIssues.length > 0) {
            console.log(`⚠️ Mapping issues resolved: ${memberMappingIssues.length} members had potential mapping conflicts`);
          }
          
          if (rateUpdateErrors.length > 0) {
            console.warn(`❌ Rate update errors: ${rateUpdateErrors.join('; ')}`);
          }
          
          if (memberUpdateErrors.length > 0) {
            console.warn(`❌ Member update errors: ${memberUpdateErrors.join('; ')}`);
          }
        }
    } catch (err) {
      console.error('Error updating rate:', err);
      setError('Failed to update rate');
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm.company || !editForm.function || !editForm.role || !editForm.location || !editForm.dailyRate || !editForm.annualWorkingDays || !editForm.year) {
      setError('All fields are required');
      return;
    }

    try {
      const updatedRate = {
        ...editForm,
        dailyRate: parseFloat(editForm.dailyRate),
        annualWorkingDays: parseInt(editForm.annualWorkingDays),
        year: parseInt(editForm.year)
      };

      await api.updateRate(editingId, updatedRate);
      markRatesUpdated();
      setEditingId(null);
      setEditForm({
        company: '',
        function: '',
        role: '',
        location: '',
        dailyRate: '',
        annualWorkingDays: '',
        year: new Date().getFullYear()
      });
      await fetchRates();
      setError('');
    } catch (err) {
      console.error('Error updating rate:', err);
      setError('Failed to update rate');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({
      company: '',
      function: '',
      role: '',
      location: '',
      dailyRate: '',
      annualWorkingDays: '',
      year: new Date().getFullYear()
    });
  };

  const handleDelete = async (index) => {
    if (window.confirm('Are you sure you want to delete this rate?')) {
      try {
        await api.deleteRate(index);
        await fetchRates();
        setError('');
      } catch (err) {
        console.error('Error deleting rate:', err);
        setError('Failed to delete rate');
      }
    }
  };

  const handleAddNew = async () => {
    if (!newRate.company || !newRate.function || !newRate.role || !newRate.location || !newRate.dailyRate || !newRate.annualWorkingDays || !newRate.year) {
      setError('All fields are required');
      return;
    }

    try {
      const rateToAdd = {
        ...newRate,
        dailyRate: parseFloat(newRate.dailyRate),
        annualWorkingDays: parseInt(newRate.annualWorkingDays),
        year: parseInt(newRate.year)
      };

      await api.createRate(rateToAdd);
      markRatesUpdated();

      // Check if this is a current year rate that should trigger member propagation
      const currentYear = new Date().getFullYear();
      const isCurrentYearRate = rateToAdd.year === currentYear;
      
      if (isCurrentYearRate) {
        console.log(`New rate added for current year (${currentYear}), checking for member propagation...`);
        
        // Find members that would be affected by this new rate
        const rateInfo = {
          company: rateToAdd.company,
          location: rateToAdd.location,
          role: rateToAdd.role
        };
        
        const affectedMembers = await findMembersAffectedByRate(rateInfo);
        
        if (affectedMembers.length > 0) {
          console.log(`Propagating new rate to outcomes for ${affectedMembers.length} affected members...`);
          
          // Propagate changes in the background
          propagateRateChangeToOutcomes(rateInfo, affectedMembers)
            .then(result => {
              if (result.success) {
                console.log(`✅ New rate propagation completed: ${result.totalUpdated} outcome updates`);
              } else {
                console.warn(`⚠️ New rate propagation completed with issues:`, result);
              }
              window.dispatchEvent(new CustomEvent('outcomeCachesInvalidated'));
            })
            .catch(error => {
              console.error(`❌ New rate propagation failed:`, error);
            });
        }
      }
      
      setDialogOpen(false);
                  setNewRate({
        company: '',
        function: '',
        role: '',
        location: '',
        dailyRate: '',
        annualWorkingDays: '',
        year: new Date().getFullYear(),
        startDate: '',
        endDate: '',
        status: 'Active'
      });
      await fetchRates();
      setError('');
    } catch (err) {
      console.error('Error creating rate:', err);
      setError('Failed to create rate');
    }
  };

  // Handle adding rate for specific year to existing role group
  const handleOpenAddYearDialog = (roleGroup) => {
    setSelectedRoleGroup(roleGroup);
    
    // Pre-fill with most recent rate data for this role
    const mostRecentRate = roleGroup.years[0]; // Already sorted newest first
    setNewYearRate({
      year: new Date().getFullYear(),
      dailyRate: mostRecentRate.dailyRate.toString(),
      annualWorkingDays: mostRecentRate.annualWorkingDays.toString()
    });
    setAddYearDialogOpen(true);
  };

  const handleAddYearRate = async () => {
    if (!selectedRoleGroup || !newYearRate.year || !newYearRate.dailyRate || !newYearRate.annualWorkingDays) {
      setError('All fields are required');
      return;
    }

    try {
      const rateToAdd = {
        company: selectedRoleGroup.company,
        function: selectedRoleGroup.function,
        role: selectedRoleGroup.role,
        location: selectedRoleGroup.location,
        dailyRate: parseFloat(newYearRate.dailyRate),
        annualWorkingDays: parseInt(newYearRate.annualWorkingDays),
        year: parseInt(newYearRate.year)
      };

      await api.createRate(rateToAdd);
      markRatesUpdated();

      // Check if this is a current year rate that should trigger member propagation
      const currentYear = new Date().getFullYear();
      const isCurrentYearRate = rateToAdd.year === currentYear;
      
      if (isCurrentYearRate) {
        console.log(`New year rate added for current year (${currentYear}), checking for member propagation...`);
        
        // Find members that would be affected by this new rate
        const rateInfo = {
          company: selectedRoleGroup.company,
          location: selectedRoleGroup.location,
          role: selectedRoleGroup.role
        };
        
        const affectedMembers = await findMembersAffectedByRate(rateInfo);
        
        if (affectedMembers.length > 0) {
          console.log(`Propagating new year rate to outcomes for ${affectedMembers.length} affected members...`);
          
          // Propagate changes in the background
          propagateRateChangeToOutcomes(rateInfo, affectedMembers)
            .then(result => {
              if (result.success) {
                console.log(`✅ New year rate propagation completed: ${result.totalUpdated} outcome updates`);
              } else {
                console.warn(`⚠️ New year rate propagation completed with issues:`, result);
              }
              window.dispatchEvent(new CustomEvent('outcomeCachesInvalidated'));
            })
            .catch(error => {
              console.error(`❌ New year rate propagation failed:`, error);
            });
        }
      }
      
      setAddYearDialogOpen(false);
      setSelectedRoleGroup(null);
      setNewYearRate({
        year: new Date().getFullYear(),
        dailyRate: '',
        annualWorkingDays: ''
      });
      await fetchRates();
      setError('');
    } catch (err) {
      console.error('Error creating year rate:', err);
      setError('Failed to create rate for year');
    }
  };

  const toggleGroupExpansion = (groupKey) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

    const uniqueCompanies = [...new Set(rates.map(rate => rate.company))].sort();
  const uniqueLocations = [...new Set(rates.map(rate => rate.location))].sort();
  const uniqueYears = [...new Set(rates.map(rate => rate.year || 2025))].sort((a, b) => b - a);
  const uniqueStatuses = [...new Set(rates.map(rate => rate.status || 'Active'))].sort();

  const groupedRates = groupRatesByRole();
  const filteredGroups = Object.entries(groupedRates).filter(([key, group]) => {
    // Apply filters to the group
    const matchesCompany = companyFilter === 'All' || group.company === companyFilter;
    const matchesLocation = locationFilter === 'All' || group.location === locationFilter;
    const matchesSearch = !searchTerm || 
      group.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.function.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.location.toLowerCase().includes(searchTerm.toLowerCase());
        const hasMatchingYear = yearFilter === 'All' || 
      group.years.some(yearRate => yearRate.year === parseInt(yearFilter));
    const hasMatchingStatus = statusFilter === 'All' || 
      group.years.some(yearRate => (yearRate.status || 'Active') === statusFilter);
    
    return matchesCompany && matchesLocation && matchesSearch && hasMatchingYear && hasMatchingStatus;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">Rates Management</Typography>
          {propagatingChanges && (
            <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
              🔄 Propagating rate changes to outcomes...
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
          disabled={propagatingChanges}
        >
          Add New Rate
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <TextField
            label="Search Rates"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            size="small"
            placeholder="Search by company, function, role, or location"
          />
        </Grid>
        <Grid item xs={12} md={2.25}>
          <FormControl fullWidth size="small">
            <InputLabel>Company</InputLabel>
            <Select
              value={companyFilter}
              label="Company"
              onChange={(e) => setCompanyFilter(e.target.value)}
            >
              <MenuItem value="All">All Companies</MenuItem>
              {uniqueCompanies.map(company => (
                <MenuItem key={company} value={company}>{company}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2.25}>
          <FormControl fullWidth size="small">
            <InputLabel>Location</InputLabel>
            <Select
              value={locationFilter}
              label="Location"
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <MenuItem value="All">All Locations</MenuItem>
              {uniqueLocations.map(location => (
                <MenuItem key={location} value={location}>{location}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
                <Grid item xs={12} md={1.8}>
          <FormControl fullWidth size="small">
            <InputLabel>Year</InputLabel>
            <Select
              value={yearFilter}
              label="Year"
              onChange={(e) => setYearFilter(e.target.value)}
            >
              <MenuItem value="All">All Years</MenuItem>
              {uniqueYears.map(year => (
                <MenuItem key={year} value={year.toString()}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={1.8}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="All">All Status</MenuItem>
              {uniqueStatuses.map(status => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={1.65}>
          <Card variant="outlined" sx={{ p: 1, bgcolor: 'info.light', color: 'info.contrastText' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon fontSize="small" />
              <Typography variant="caption" fontWeight={600}>
                COLA Rate: {colaRate}%
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box sx={{ mb: 2 }}>
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Year-based Rates:</strong> Rates are grouped by role and company. 
            When a specific year rate isn't available, the system will use the most recent rate 
            and apply COLA adjustments ({colaRate}% annually) to estimate the target year rate.
          </Typography>
        </Alert>
      </Box>

            <Box sx={{ mb: 2 }}>
        <Alert severity="warning">
          <Typography variant="body2">
            <strong>Role Mapping:</strong> IT roster member roles are automatically mapped to rate roles. 
            For example, "Sr Dev" maps to "Fullstack Developer". When editing role names, 
            consider how this affects member-to-rate mappings.
          </Typography>
        </Alert>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Automatic Propagation:</strong> When rates are updated or added for the current year ({new Date().getFullYear()}), 
            affected IT roster members will automatically have their information updated across all assigned outcomes. 
            This ensures rate calculations remain accurate.
          </Typography>
        </Alert>
      </Box>

      {/* Grouped Rates Display */}
      <Box>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography>Loading rates...</Typography>
          </Box>
        ) : filteredGroups.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {searchTerm || companyFilter !== 'All' || locationFilter !== 'All' || yearFilter !== 'All' 
                ? 'No rates match your filters' 
                : 'No rates found'
              }
            </Typography>
          </Box>
        ) : (
          filteredGroups.map(([groupKey, group]) => (
            <Accordion 
              key={groupKey}
              expanded={expandedGroups.has(groupKey)}
              onChange={() => toggleGroupExpansion(groupKey)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      label={group.company}
                      color="success"
                      variant="outlined"
                      size="small"
                    />
                    <Chip
                      label={group.location}
                      color="warning"
                      variant="outlined"
                      size="small"
                    />
                    <Chip
                      label={group.function}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
                    {group.role}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {group.years.length} year{group.years.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Historical rates for {group.role} at {group.company} ({group.location})
                  </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SettingsIcon />}
                        onClick={() => handleOpenEditDialog(group.years[0], group.years[0].originalIndex)}
                        sx={{ fontSize: '0.75rem' }}
                        color="primary"
                      >
                        Role Edit
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenAddYearDialog(group)}
                        sx={{ fontSize: '0.75rem' }}
                      >
                        Add Rate for Year
                      </Button>
                    </Box>
                    
                    {/* Show roster roles that map to this rate role */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end', maxWidth: 300 }}>
                      {getRosterRolesForRateRole(group.role).map(rosterRole => (
                        <Chip
                          key={rosterRole}
                          label={rosterRole}
                          size="small"
                          variant="outlined"
                          color="info"
                          sx={{ fontSize: '0.65rem', height: '20px' }}
                        />
                      ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      Roster roles that map to this rate
                    </Typography>
                  </Box>
                </Box>
                
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                                                <TableCell>Year</TableCell>
                        <TableCell align="right">Daily Rate</TableCell>
                        <TableCell align="right">Annual Working Days</TableCell>
                        <TableCell align="right">Annual Rate</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">COLA Projections</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                                            {group.years
                        .filter(yearRate => {
                          const matchesYear = yearFilter === 'All' || yearRate.year === parseInt(yearFilter);
                          const matchesStatus = statusFilter === 'All' || (yearRate.status || 'Active') === statusFilter;
                          return matchesYear && matchesStatus;
                        })
                        .map((yearRate) => (
                        <TableRow key={`${groupKey}-${yearRate.year}`}>
                          <TableCell>
                            {editingId === yearRate.originalIndex ? (
                              <TextField
                                type="number"
                                value={editForm.year}
                                onChange={(e) => setEditForm({ ...editForm, year: parseInt(e.target.value) })}
                                size="small"
                                inputProps={{ min: 2020, max: 2030 }}
                              />
                            ) : (
                              <Chip
                                label={yearRate.year}
                                color="info"
                                variant="outlined"
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {editingId === yearRate.originalIndex ? (
                              <TextField
                                value={editForm.dailyRate}
                                onChange={(e) => setEditForm({ ...editForm, dailyRate: e.target.value })}
                                size="small"
                                type="number"
                                inputProps={{ step: "0.01" }}
                              />
                            ) : (
                              formatCurrency(yearRate.dailyRate)
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {editingId === yearRate.originalIndex ? (
                              <TextField
                                value={editForm.annualWorkingDays}
                                onChange={(e) => setEditForm({ ...editForm, annualWorkingDays: e.target.value })}
                                size="small"
                                type="number"
                              />
                            ) : (
                              yearRate.annualWorkingDays
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {editingId === yearRate.originalIndex ? (
                              formatCurrency(parseFloat(editForm.dailyRate || 0) * parseInt(editForm.annualWorkingDays || 0))
                            ) : (
                              formatCurrency(yearRate.dailyRate * yearRate.annualWorkingDays)
                            )}
                                                    </TableCell>
                          <TableCell>
                            <Chip
                              label={yearRate.status || 'Active'}
                              color={yearRate.status === 'Archived' ? 'default' : 'success'}
                              variant="outlined"
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {[yearRate.year + 1, yearRate.year + 2].map(projYear => (
                                <Tooltip 
                                  key={projYear}
                                  title={`${projYear} projection using ${colaRate}% COLA`}
                                >
                                  <Typography variant="caption" color="text.secondary">
                                    {projYear}: {formatCurrency(calculateCOLAAdjustedRate(yearRate.dailyRate, yearRate.year, projYear, colaRate))}
                                  </Typography>
                                </Tooltip>
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            {editingId === yearRate.originalIndex ? (
                              <Box>
                                <IconButton
                                  onClick={handleSaveEdit}
                                  color="primary"
                                  size="small"
                                >
                                  <SaveIcon />
                                </IconButton>
                                <IconButton
                                  onClick={handleCancelEdit}
                                  color="secondary"
                                  size="small"
                                >
                                  <CancelIcon />
                                </IconButton>
                              </Box>
                                                        ) : (
                              <Box>
                                                                                                <Tooltip title="Rate Edit (inline)">
                                  <IconButton
                                    onClick={() => handleEdit(yearRate, yearRate.originalIndex)}
                                    sx={{ color: "#90caf9" }}
                                    size="small"
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <IconButton
                                  onClick={() => handleDelete(yearRate.originalIndex)}
                                  color="error"
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Box>

            {/* Add New Rate Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Rate</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Company */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={companyOptions}
                value={newRate.company}
                onChange={(event, newValue) => {
                  setNewRate({ ...newRate, company: newValue || '' });
                }}
                onInputChange={(event, newInputValue) => {
                  setNewRate({ ...newRate, company: newInputValue });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Company"
                    required
                    helperText="Select existing or type new company"
                  />
                )}
              />
            </Grid>
            
            {/* Location */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={locationOptions}
                value={newRate.location}
                onChange={(event, newValue) => {
                  setNewRate({ ...newRate, location: newValue || '' });
                }}
                onInputChange={(event, newInputValue) => {
                  setNewRate({ ...newRate, location: newInputValue });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Location"
                    required
                    helperText="Select existing or type new location"
                  />
                )}
              />
            </Grid>
            
            {/* Function */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={functionOptions}
                value={newRate.function}
                onChange={(event, newValue) => {
                  setNewRate({ ...newRate, function: newValue || '' });
                }}
                onInputChange={(event, newInputValue) => {
                  setNewRate({ ...newRate, function: newInputValue });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Function"
                    required
                    helperText="Select existing or type new function"
                  />
                )}
              />
            </Grid>
            
            {/* Role */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={roleOptions}
                value={newRate.role}
                onChange={(event, newValue) => {
                  setNewRate({ ...newRate, role: newValue || '' });
                }}
                onInputChange={(event, newInputValue) => {
                  setNewRate({ ...newRate, role: newInputValue });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Role"
                    required
                    helperText="Select existing or type new role"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Rate Details
                </Typography>
              </Divider>
            </Grid>
            
            {/* Year */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Year"
                type="number"
                value={newRate.year}
                onChange={(e) => setNewRate({ ...newRate, year: parseInt(e.target.value) })}
                fullWidth
                required
                inputProps={{ min: 2020, max: 2035 }}
              />
            </Grid>
            
            {/* Daily Rate */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Daily Rate"
                value={newRate.dailyRate}
                onChange={(e) => setNewRate({ ...newRate, dailyRate: e.target.value })}
                fullWidth
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                required
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>
                }}
              />
            </Grid>
            
            {/* Annual Working Days */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Annual Working Days"
                value={newRate.annualWorkingDays}
                onChange={(e) => setNewRate({ ...newRate, annualWorkingDays: e.target.value })}
                fullWidth
                type="number"
                inputProps={{ min: "1", max: "366" }}
                required
                helperText="Typical: 250-260 days"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Optional: Rate Validity Period
                </Typography>
              </Divider>
            </Grid>
            
            {/* Start Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Date"
                type="date"
                value={newRate.startDate}
                onChange={(e) => setNewRate({ ...newRate, startDate: e.target.value })}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Optional: When this rate becomes effective"
              />
            </Grid>
            
            {/* End Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="End Date"
                type="date"
                value={newRate.endDate}
                onChange={(e) => setNewRate({ ...newRate, endDate: e.target.value })}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Optional: When this rate expires"
                inputProps={{
                  min: newRate.startDate || undefined
                }}
              />
            </Grid>
            
                        {/* Status */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newRate.status}
                  label="Status"
                  onChange={(e) => setNewRate({ ...newRate, status: e.target.value })}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Archived">Archived</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              {/* Empty grid item for spacing */}
            </Grid>
            
            {/* Rate Preview */}
            {newRate.dailyRate && newRate.annualWorkingDays && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1, mt: 1 }}>
                  <Typography variant="body2" color="primary.contrastText">
                    <strong>Estimated Annual Rate:</strong> {formatCurrency(parseFloat(newRate.dailyRate || 0) * parseInt(newRate.annualWorkingDays || 0))}
                  </Typography>
                  {newRate.startDate && newRate.endDate && (
                    <Typography variant="caption" color="primary.contrastText" sx={{ display: 'block', mt: 0.5 }}>
                      Valid from {new Date(newRate.startDate).toLocaleDateString()} to {new Date(newRate.endDate).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddNew} variant="contained">Add Rate</Button>
        </DialogActions>
      </Dialog>

      {/* Add Year Rate Dialog */}
      <Dialog open={addYearDialogOpen} onClose={() => setAddYearDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add Rate for {selectedRoleGroup?.role}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Adding rate for:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Chip label={selectedRoleGroup?.company} size="small" color="success" variant="outlined" />
              <Chip label={selectedRoleGroup?.location} size="small" color="warning" variant="outlined" />
              <Chip label={selectedRoleGroup?.function} size="small" color="primary" variant="outlined" />
            </Box>
            <Typography variant="body2" fontWeight={600}>
              {selectedRoleGroup?.role}
            </Typography>
          </Box>
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Year"
                type="number"
                value={newYearRate.year}
                onChange={(e) => setNewYearRate({ ...newYearRate, year: parseInt(e.target.value) })}
                fullWidth
                required
                inputProps={{ min: 2020, max: 2030 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Daily Rate"
                value={newYearRate.dailyRate}
                onChange={(e) => setNewYearRate({ ...newYearRate, dailyRate: e.target.value })}
                fullWidth
                type="number"
                inputProps={{ step: "0.01" }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Annual Working Days"
                value={newYearRate.annualWorkingDays}
                onChange={(e) => setNewYearRate({ ...newYearRate, annualWorkingDays: e.target.value })}
                fullWidth
                type="number"
                required
              />
            </Grid>
            
            {selectedRoleGroup && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="success.contrastText">
                    <strong>Estimated Annual Rate:</strong> {formatCurrency(parseFloat(newYearRate.dailyRate || 0) * parseInt(newYearRate.annualWorkingDays || 0))}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddYearDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddYearRate} variant="contained">Add Rate</Button>
                </DialogActions>
      </Dialog>

      {/* Edit Rate Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Rate</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Company */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={companyOptions}
                value={editRateForm.company}
                onChange={(event, newValue) => {
                  setEditRateForm({ ...editRateForm, company: newValue || '' });
                }}
                onInputChange={(event, newInputValue) => {
                  setEditRateForm({ ...editRateForm, company: newInputValue });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Company"
                    required
                    helperText="Select existing or type new company"
                  />
                )}
              />
            </Grid>
            
            {/* Location */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={locationOptions}
                value={editRateForm.location}
                onChange={(event, newValue) => {
                  setEditRateForm({ ...editRateForm, location: newValue || '' });
                }}
                onInputChange={(event, newInputValue) => {
                  setEditRateForm({ ...editRateForm, location: newInputValue });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Location"
                    required
                    helperText="Select existing or type new location"
                  />
                )}
              />
            </Grid>
            
            {/* Function */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={functionOptions}
                value={editRateForm.function}
                onChange={(event, newValue) => {
                  setEditRateForm({ ...editRateForm, function: newValue || '' });
                }}
                onInputChange={(event, newInputValue) => {
                  setEditRateForm({ ...editRateForm, function: newInputValue });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Function"
                    required
                    helperText="Select existing or type new function"
                  />
                )}
              />
            </Grid>
            
                        {/* Role */}
            <Grid item xs={12} sm={6}>
              {editingRoleName ? (
                <TextField
                  label="Role Name"
                  value={tempRoleName}
                  onChange={(e) => setTempRoleName(e.target.value)}
                  fullWidth
                  required
                  helperText="Editing role name will update all rates and members with this role"
                  InputProps={{
                    endAdornment: (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditRateForm({ ...editRateForm, role: tempRoleName });
                            setEditingRoleName(false);
                          }}
                          color="primary"
                        >
                          <SaveIcon sx={{ fontSize: '0.8rem' }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setTempRoleName(editRateForm.role);
                            setEditingRoleName(false);
                          }}
                          color="secondary"
                        >
                          <CancelIcon sx={{ fontSize: '0.8rem' }} />
                        </IconButton>
                      </Box>
                    )
                  }}
                />
              ) : (
                <TextField
                  label="Role"
                  value={editRateForm.role}
                  fullWidth
                  required
                  disabled
                  helperText="Click edit icon to modify role name"
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setTempRoleName(editRateForm.role);
                          setEditingRoleName(true);
                        }}
                        color="primary"
                      >
                        <EditIcon sx={{ fontSize: '0.8rem' }} />
                      </IconButton>
                    )
                  }}
                />
              )}
              {editingRoleName && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="caption">
                    <strong>Warning:</strong> Changing this role name will update all rates and IT roster members 
                    with the combination: {editingRate?.company}/{editingRate?.location}/{editingRate?.role}
                  </Typography>
                </Alert>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Rate Details
                </Typography>
              </Divider>
            </Grid>
            
            {/* Year */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Year"
                type="number"
                value={editRateForm.year}
                onChange={(e) => setEditRateForm({ ...editRateForm, year: parseInt(e.target.value) })}
                fullWidth
                required
                inputProps={{ min: 2020, max: 2035 }}
              />
            </Grid>
            
            {/* Daily Rate */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Daily Rate"
                value={editRateForm.dailyRate}
                onChange={(e) => setEditRateForm({ ...editRateForm, dailyRate: e.target.value })}
                fullWidth
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                required
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>
                }}
              />
            </Grid>
            
            {/* Annual Working Days */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Annual Working Days"
                value={editRateForm.annualWorkingDays}
                onChange={(e) => setEditRateForm({ ...editRateForm, annualWorkingDays: e.target.value })}
                fullWidth
                type="number"
                inputProps={{ min: "1", max: "366" }}
                required
                helperText="Typical: 250-260 days"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Status & Validity Period
                </Typography>
              </Divider>
            </Grid>
            
            {/* Status */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editRateForm.status}
                  label="Status"
                  onChange={(e) => setEditRateForm({ ...editRateForm, status: e.target.value })}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Archived">Archived</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Start Date */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Start Date"
                type="date"
                value={editRateForm.startDate}
                onChange={(e) => setEditRateForm({ ...editRateForm, startDate: e.target.value })}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Optional: When this rate becomes effective"
              />
            </Grid>
            
            {/* End Date */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="End Date"
                type="date"
                value={editRateForm.endDate}
                onChange={(e) => setEditRateForm({ ...editRateForm, endDate: e.target.value })}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Optional: When this rate expires"
                inputProps={{
                  min: editRateForm.startDate || undefined
                }}
              />
            </Grid>
            
            {/* Rate Preview */}
            {editRateForm.dailyRate && editRateForm.annualWorkingDays && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1, mt: 1 }}>
                  <Typography variant="body2" color="primary.contrastText">
                    <strong>Estimated Annual Rate:</strong> {formatCurrency(parseFloat(editRateForm.dailyRate || 0) * parseInt(editRateForm.annualWorkingDays || 0))}
                  </Typography>
                  {editRateForm.startDate && editRateForm.endDate && (
                    <Typography variant="caption" color="primary.contrastText" sx={{ display: 'block', mt: 0.5 }}>
                      Valid from {new Date(editRateForm.startDate).toLocaleDateString()} to {new Date(editRateForm.endDate).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
                <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setEditingRoleName(false);
            setTempRoleName('');
          }}>Cancel</Button>
          <Button onClick={handleSaveEditDialog} variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RatesTab;